/* ================================================= */
/* ASSISTANT TONE                                    */
/* Forbidden phrase maps, tone normalization, and   */
/* corporate-to-pastoral replacement logic.         */
/*                                                   */
/* All functions: pure, synchronous, no side effects */
/* Input/output: strings only                        */
/* ================================================= */

/* ============================= */
/* CORPORATE → PASTORAL MAP      */
/* ============================= */

const CORPORATE_TO_PASTORAL = [
  ["gestionar",          "atender"],
  ["implementar",        "llevar a cabo"],
  ["ejecutar",           "realizar"],
  ["optimizar",          "cuidar mejor"],
  ["monitorear",         "dar seguimiento a"],
  ["seguimiento estratégico", "seguimiento pastoral"],
  ["acción correctiva",  "atención pastoral"],
  ["plan de acción",     "próximo paso"],
  ["métricas",           "señales"],
  ["indicadores",        "señales"],
  ["kpi",                "señal"],
  ["performance",        "ritmo"],
  ["rendimiento",        "ritmo"],
  ["eficiencia",         "constancia"],
  ["productividad",      "fidelidad"],
  ["objetivo",           "intención pastoral"],
  ["target",             "intención pastoral"],
  ["priorizar",          "atender primero"],
  ["escalado",           "atención especial"],
  ["escalamiento",       "atención especial"],
  ["intervención",       "visita pastoral"],
  ["caso",               "persona"],
  ["recurso",            "tiempo pastoral"],
  ["asignación",         "atención"],
  ["reportar",           "comunicar"],
  ["registrar",          "anotar"],
  ["trackear",           "dar seguimiento"],
  ["trackeado",          "con seguimiento"],
];

/* ============================= */
/* SOFTENING MAP                 */
/* ============================= */

const SOFTENING_MAP = [
  ["debe visitar urgentemente",  "sería importante visitar pronto"],
  ["debe ser visitado urgentemente", "merece una visita pronto"],
  ["es crítico que",             "sería importante que"],
  ["es urgente que",             "convendría que"],
  ["está atrasado",              "el ritmo ha disminuido"],
  ["está en mora",               "ha pasado tiempo desde la última visita"],
  ["incumplido",                 "pendiente de atención"],
  ["incumplimiento",             "oportunidad de retomar el ritmo"],
  ["overdue",                    "con tiempo de espera"],
  ["sin visitar",                "esperando una visita"],
  ["no ha sido visitado",        "aún no ha recibido visita reciente"],
  ["alerta crítica",             "señal de atención"],
  ["riesgo alto",                "requiere atención pastoral"],
  ["situación crítica",          "momento de atención especial"],
  ["abandonado",                 "con necesidad de acompañamiento"],
  ["pendiente",                  "en espera de seguimiento"],
  ["atrasado",                   "con tiempo acumulado"],
  ["incompleto",                 "en proceso"],
  ["fallido",                    "en pausa"],
  ["fracasó",                    "necesita retomarse"],
  ["fracasado",                  "listo para retomarse"],
  ["deadline",                   "momento oportuno"],
  ["vencido",                    "listo para retomarse"],
  ["sobrecargado",               "con mucho por atender"],
  ["colapsado",                  "necesitado de acompañamiento"],
  ["crisis",                     "momento difícil"],
  ["en crisis",                  "atravesando un momento difícil"],
];

/* ============================= */
/* FORBIDDEN PHRASES             */
/* These should not appear at all */
/* ============================= */

const FORBIDDEN_PHRASES = [
  "task overdue",
  "SLA",
  "sprint",
  "backlog",
  "accionable",
  "synergy",
  "leverage",
  "quick win",
  "bandwidth",
  "stakeholder",
  "roadmap",
  "milestone",
  "deliverable",
];

/* ============================= */
/* TONE TRANSFORMS               */
/* ============================= */

/**
 * stripCorporateLanguage — replaces corporate vocabulary with pastoral equivalents.
 */
export function stripCorporateLanguage(text) {
  if (!text || typeof text !== "string") return text;
  let result = text;
  for (const [corporate, pastoral] of CORPORATE_TO_PASTORAL) {
    result = result.replace(new RegExp(corporate, "gi"), (match) => {
      const isCapitalized = match[0] === match[0].toUpperCase() && match[0] !== match[0].toLowerCase();
      return isCapitalized
        ? pastoral.charAt(0).toUpperCase() + pastoral.slice(1)
        : pastoral;
    });
  }
  for (const phrase of FORBIDDEN_PHRASES) {
    result = result.replace(new RegExp(phrase, "gi"), "");
  }
  return result.replace(/\s{2,}/g, " ").trim();
}

/**
 * softenUrgentLanguage — reduces alarmist phrasing to calm pastoral language.
 */
export function softenUrgentLanguage(text) {
  if (!text || typeof text !== "string") return text;
  let result = text;
  for (const [urgent, soft] of SOFTENING_MAP) {
    result = result.replace(new RegExp(urgent, "gi"), (match) => {
      const isCapitalized = match[0] === match[0].toUpperCase() && match[0] !== match[0].toLowerCase();
      return isCapitalized
        ? soft.charAt(0).toUpperCase() + soft.slice(1)
        : soft;
    });
  }
  return result;
}

/**
 * applyPastoralCadence — ensures sentences end with a period, trims extra spaces.
 */
export function applyPastoralCadence(text) {
  if (!text || typeof text !== "string") return text;
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  const lastChar = trimmed[trimmed.length - 1];
  if (!['.', '!', '?', ':'].includes(lastChar)) return trimmed + ".";
  return trimmed;
}

/**
 * normalizePastoralTone — applies all transforms in order.
 * Strips corporate → softens urgent → ensures cadence.
 */
export function normalizePastoralTone(text) {
  if (!text || typeof text !== "string") return text;
  return applyPastoralCadence(
    softenUrgentLanguage(
      stripCorporateLanguage(text)
    )
  );
}

/**
 * normalizePastoralArray — applies normalizePastoralTone to an array of strings or objects with a `message` field.
 */
export function normalizePastoralArray(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    if (typeof item === "string") return normalizePastoralTone(item);
    if (item && typeof item === "object") {
      const normalized = { ...item };
      if (normalized.message) normalized.message = normalizePastoralTone(normalized.message);
      if (normalized.reason)  normalized.reason  = normalizePastoralTone(normalized.reason);
      if (normalized.label)   normalized.label   = normalizePastoralTone(normalized.label);
      return normalized;
    }
    return item;
  });
}
